import React from 'react';
import PropTypes from 'prop-types';
import { datadogRum } from '@datadog/browser-rum';
import { Box, Text } from '@usealloy/component-library';

import log from '~/helpers/logging';


const ErrorBoundaryPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  errorChildren: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.bool,
  ]),
};

const ErrorBoundaryDefaultProps = {
  errorChildren: false,
};

class ErrorBoundary extends React.PureComponent {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, errorInfo) {
    const context = {
      error: { ...errorInfo },
      originatedUrl: window.location.pathname,
      url: window.location.pathname,
      pageCrashed: true,
      errorScreen: 'Something went wrong',
    };

    // Extract component name from errorInfo if available
    if (errorInfo && errorInfo.componentStack) {
      // Look for component names in the stack trace
      // Common patterns: "in ComponentName", "at ComponentName", "ComponentName.render"
      const componentPatterns = [
        /in\s+([A-Z][a-zA-Z0-9]*)/, // "in ComponentName"
        /at\s+([A-Z][a-zA-Z0-9]*)/, // "at ComponentName"
        /([A-Z][a-zA-Z0-9]*)\.render/, // "ComponentName.render"
        /([A-Z][a-zA-Z0-9]*)\s+\(/, // "ComponentName ("
      ];

      for (const pattern of componentPatterns) {
        const [, componentName] = errorInfo.componentStack.match(pattern) || [];
        if (componentName) {
          context.errorComponent = componentName;
          break;
        }
      }
    }

    // Send error to Datadog so we can monitor page crashes
    datadogRum.addError(error, context);
    log('error', error.message, context, error);
  }

  render() {
    const { children, errorChildren } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return errorChildren || (
        <Box direction="column" alignItems="flex-start" gap={2} style={{ margin: 'var(--cl-space-6)' }}>
          <Text as="h1" size={5} weight={500}>Something went wrong</Text>
          <Text as="h2" size={4} weight={400}>{'This page isn\'t working right now. Please try again or contact support.'}</Text>
        </Box>
      );
    }

    return children;
  }
}
ErrorBoundary.propTypes = ErrorBoundaryPropTypes;
ErrorBoundary.defaultProps = ErrorBoundaryDefaultProps;

export default ErrorBoundary;
